import React, { useState } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import {
  FiGrid,
  FiUsers,
  FiSend,
  FiBarChart2,
  FiSettings,
  FiLogOut,
  FiMenu,
  FiX,
} from 'react-icons/fi';
import { setUserRole } from 'redux_store/slices/userRole';
import { clearAuth } from 'redux_store/slices/authSlice';
import tokenManager from 'services/auth/tokenManager';

const NAV_ITEMS = [
  { to: '/platform/dashboard', label: 'Dashboard', icon: FiGrid },
  { to: '/platform/companies', label: 'Companies', icon: FiUsers },
  { to: '/platform/invitations', label: 'Invitations', icon: FiSend },
  { to: '/platform/analytics', label: 'Analytics', icon: FiBarChart2 },
  { to: '/platform/settings', label: 'Settings', icon: FiSettings },
];

const PlatformLayout = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = async () => {
    tokenManager.clearTokens();
    dispatch(clearAuth());
    dispatch(setUserRole(null));
    navigate('/platform/login');
  };

  const linkClasses = ({ isActive }) =>
    `flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
      isActive
        ? 'bg-primary50 text-primary600'
        : 'text-neutral600 hover:bg-neutral100 hover:text-secondary900'
    }`;

  const Sidebar = () => (
    <aside className="w-64 bg-white border-r border-neutral200 min-h-screen flex flex-col">
      {/* Logo */}
      <div className="p-5 border-b border-neutral200">
        <div className="flex items-center gap-3">
          <img
            src="/fundmoreai.png"
            alt="Fundmore"
            className="h-8 w-auto"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
          <span className="text-sm font-semibold text-neutral500 uppercase tracking-wider">
            Platform Admin
          </span>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-4 space-y-1">
        {NAV_ITEMS.map(({ to, label, icon: Icon }) => (
          <NavLink
            key={to}
            to={to}
            className={linkClasses}
            onClick={() => setSidebarOpen(false)}
          >
            <Icon className="w-4 h-4" />
            {label}
          </NavLink>
        ))}
      </nav>

      {/* Logout */}
      <div className="p-4 border-t border-neutral200">
        <button
          onClick={handleLogout}
          className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium text-red-600 hover:bg-red-50 transition-colors w-full"
        >
          <FiLogOut className="w-4 h-4" />
          Sign Out
        </button>
      </div>
    </aside>
  );

  return (
    <div className="flex min-h-screen bg-neutral50">
      {/* Mobile menu button */}
      <button
        className="lg:hidden fixed top-4 left-4 z-50 p-2 bg-white rounded-lg shadow-md"
        onClick={() => setSidebarOpen(!sidebarOpen)}
      >
        {sidebarOpen ? <FiX size={20} /> : <FiMenu size={20} />}
      </button>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar - desktop always visible, mobile conditional */}
      <div className={`
        fixed lg:static inset-y-0 left-0 z-40
        transform transition-transform duration-200 ease-in-out
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        <Sidebar />
      </div>

      {/* Main content */}
      <main className="flex-1 p-4 sm:p-6 lg:p-8 overflow-auto">
        <Outlet />
      </main>
    </div>
  );
};

export default PlatformLayout;
